import React from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

const CartDrawer = () => {
  const { cartItems, removeFromCart, updateQty, total, cartOpen, setCartOpen } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      <Overlay $open={cartOpen} onClick={() => setCartOpen(false)} />
      <Drawer $open={cartOpen}>
        <DrawerHeader>
          <DrawerTitle>Shporta</DrawerTitle>
          <DrawerCount>{cartItems.reduce((s, i) => s + i.qty, 0)} artikuj</DrawerCount>
          <CloseBtn onClick={() => setCartOpen(false)}>✕</CloseBtn>
        </DrawerHeader>

        <DrawerBody>
          {cartItems.length === 0 ? (
            <EmptyCart>
              <EmptyIcon>🛒</EmptyIcon>
              <EmptyText>Shporta juaj është bosh</EmptyText>
              <EmptySub>Shtoni produkte për të vazhduar</EmptySub>
            </EmptyCart>
          ) : (
            cartItems.map((item) => (
              <CartItem key={item.id}>
                <ItemEmoji>{item.image}</ItemEmoji>
                <ItemInfo>
                  <ItemName>{item.name}</ItemName>
                  <ItemBrand>{item.brand}</ItemBrand>
                  <ItemPrice>€{(item.price * item.qty).toFixed(2)}</ItemPrice>
                </ItemInfo>
                <QtyControls>
                  <QtyBtn onClick={() => updateQty(item.id, item.qty - 1)}>−</QtyBtn>
                  <QtyNum>{item.qty}</QtyNum>
                  <QtyBtn onClick={() => updateQty(item.id, item.qty + 1)}>+</QtyBtn>
                </QtyControls>
                <RemoveBtn onClick={() => removeFromCart(item.id)}>✕</RemoveBtn>
              </CartItem>
            ))
          )}
        </DrawerBody>

        {cartItems.length > 0 && (
          <DrawerFooter>
            <TotalRow>
              <TotalLabel>Totali</TotalLabel>
              <TotalAmount>€{total.toFixed(2)}</TotalAmount>
            </TotalRow>
            <CheckoutBtn onClick={handleCheckout}>
              Vazhdo me Porosinë →
            </CheckoutBtn>
            <ContinueBtn onClick={() => setCartOpen(false)}>
              Vazhdo blerjen
            </ContinueBtn>
          </DrawerFooter>
        )}
      </Drawer>
    </>
  );
};

export default CartDrawer;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 998;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "all" : "none")};
  transition: opacity 0.3s ease;
  backdrop-filter: blur(4px);
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(420px, 100vw);
  background: #0f0f0f;
  border-left: 1px solid rgba(139, 58, 26, 0.2);
  z-index: 999;
  display: flex;
  flex-direction: column;
  animation: ${({ $open }) => ($open ? slideIn : slideOut)} 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.8rem 2rem;
  border-bottom: 1px solid rgba(139, 58, 26, 0.15);
`;

const DrawerTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 1.6rem;
  color: var(--white);
  flex: 1;
`;

const DrawerCount = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--accent);
  text-transform: uppercase;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: var(--white-dim);
  font-size: 1rem;
  cursor: pointer;
  padding: 0.3rem;
  transition: color 0.2s;
  &:hover { color: var(--white); }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 2px; }
`;

const EmptyCart = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  padding: 4rem 0;
`;

const EmptyIcon = styled.span`
  font-size: 3rem;
  opacity: 0.3;
`;

const EmptyText = styled.p`
  font-family: var(--font-display);
  font-size: 1.2rem;
  color: var(--white);
`;

const EmptySub = styled.p`
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--white-dim);
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem;
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(139,58,26,0.1);
  border-radius: 4px;
  transition: border-color 0.2s;
  &:hover { border-color: rgba(139,58,26,0.3); }
`;

const ItemEmoji = styled.span`
  font-size: 2rem;
  flex-shrink: 0;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.p`
  font-family: var(--font-condensed);
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--white);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemBrand = styled.p`
  font-family: var(--font-body);
  font-size: 0.72rem;
  color: var(--white-dim);
  margin-top: 2px;
`;

const ItemPrice = styled.p`
  font-family: var(--font-display);
  font-size: 1rem;
  color: var(--accent);
  margin-top: 4px;
`;

const QtyControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QtyBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid rgba(139,58,26,0.3);
  background: transparent;
  color: var(--white);
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover { background: var(--accent); border-color: var(--accent); }
`;

const QtyNum = styled.span`
  font-family: var(--font-display);
  font-size: 1rem;
  color: var(--white);
  min-width: 20px;
  text-align: center;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: rgba(240,236,230,0.25);
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0.2rem;
  transition: color 0.2s;
  &:hover { color: #cc3333; }
`;

const DrawerFooter = styled.div`
  padding: 1.5rem 2rem 2rem;
  border-top: 1px solid rgba(139,58,26,0.15);
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
`;

const TotalLabel = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--white-dim);
`;

const TotalAmount = styled.span`
  font-family: var(--font-display);
  font-size: 1.8rem;
  color: var(--white);
`;

const CheckoutBtn = styled.button`
  width: 100%;
  padding: 1rem;
  background: var(--accent);
  border: none;
  border-radius: 999px;
  font-family: var(--font-condensed);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--white);
  cursor: pointer;
  transition: background 0.25s ease, transform 0.2s ease;
  &:hover { background: var(--accent-light); transform: scale(1.02); }
`;

const ContinueBtn = styled.button`
  width: 100%;
  padding: 0.8rem;
  background: transparent;
  border: 1px solid rgba(139,58,26,0.25);
  border-radius: 999px;
  font-family: var(--font-condensed);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--white-dim);
  cursor: pointer;
  transition: all 0.22s ease;
  &:hover { border-color: var(--accent); color: var(--white); }
`;
